/**
 * useMobile Hook - Detects mobile devices and provides touch interaction handlers
 * Handles long-press and double-tap gestures for mobile interfaces
 */

import { useState, useEffect, useCallback } from 'react';
import type { TouchHandlers } from '../types/grid';

/**
 * Return type for useMobile hook
 */
export interface UseMobileReturn {
  /** Whether device is mobile */
  isMobile: boolean;
  /** Whether device supports touch */
  isTouch: boolean;
  /** Create long-press handlers for an element */
  createLongPressHandlers: (
    onLongPress: () => void,
    threshold?: number
  ) => TouchHandlers;
  /** Create double-tap handlers for an element */
  createDoubleTapHandlers: (
    onDoubleTap: () => void,
    delay?: number
  ) => TouchHandlers;
}

/**
 * Long press threshold in milliseconds
 */
const DEFAULT_LONG_PRESS_THRESHOLD = 500;

/**
 * Double tap delay in milliseconds
 */
const DEFAULT_DOUBLE_TAP_DELAY = 300;

/**
 * Movement threshold in pixels to cancel long press
 */
const MOVEMENT_THRESHOLD = 10;

/**
 * Detect if device is mobile
 */
function detectMobile(): boolean {
  if (typeof window === 'undefined') return false;

  // Check user agent
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  
  if (mobileRegex.test(userAgent.toLowerCase())) {
    return true;
  }

  // Check for touch support and small screen
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;
  
  return hasTouch && isSmallScreen;
}

/**
 * Detect if device supports touch
 */
function detectTouch(): boolean {
  if (typeof window === 'undefined') return false;
  
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
}

/**
 * Custom hook for mobile device detection and touch interactions
 * 
 * @returns Mobile detection state and touch handlers
 */
export function useMobile(): UseMobileReturn {
  const [isMobile, setIsMobile] = useState(() => detectMobile());
  const [isTouch, setIsTouch] = useState(() => detectTouch());

  /**
   * Update mobile detection on resize
   */
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(detectMobile());
      setIsTouch(detectTouch());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /**
   * Create long-press handlers
   * 
   * @param onLongPress - Callback when long press is detected
   * @param threshold - Long press duration threshold in ms
   * @returns Touch event handlers
   */
  const createLongPressHandlers = useCallback(
    (
      onLongPress: () => void,
      threshold: number = DEFAULT_LONG_PRESS_THRESHOLD
    ): TouchHandlers => {
      const timerRef = { current: null as number | null };
      const startPosRef = { current: null as { x: number; y: number } | null };
      const longPressTriggeredRef = { current: false };
      const elementRef = { current: null as HTMLElement | null };

      const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        
        startPosRef.current = { x: touch.clientX, y: touch.clientY };
        longPressTriggeredRef.current = false;
        elementRef.current = e.currentTarget as HTMLElement;

        // Start long press timer
        timerRef.current = window.setTimeout(() => {
          longPressTriggeredRef.current = true;
          
          // Add visual feedback class
          if (elementRef.current) {
            elementRef.current.classList.add('long-press-active');
          }
          
          // Trigger haptic feedback if available
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
          
          // Brief delay for visual feedback, then trigger callback
          setTimeout(() => {
            if (elementRef.current) {
              elementRef.current.classList.remove('long-press-active');
            }
            onLongPress();
          }, 100);
        }, threshold);
      };

      const handleTouchMove = (e: React.TouchEvent) => {
        if (!startPosRef.current || !timerRef.current) return;

        const touch = e.touches[0];
        if (!touch) return;
        
        const deltaX = Math.abs(touch.clientX - startPosRef.current.x);
        const deltaY = Math.abs(touch.clientY - startPosRef.current.y);

        // Cancel long press if moved too much
        if (deltaX > MOVEMENT_THRESHOLD || deltaY > MOVEMENT_THRESHOLD) {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          if (elementRef.current) {
            elementRef.current.classList.remove('long-press-active');
          }
        }
      };

      const handleTouchEnd = () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        if (elementRef.current) {
          elementRef.current.classList.remove('long-press-active');
        }
        startPosRef.current = null;
        elementRef.current = null;
      };

      const handleTouchCancel = () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        if (elementRef.current) {
          elementRef.current.classList.remove('long-press-active');
        }
        startPosRef.current = null;
        elementRef.current = null;
      };

      return {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        onTouchCancel: handleTouchCancel,
      };
    },
    []
  );

  /**
   * Create double-tap handlers
   * 
   * @param onDoubleTap - Callback when double tap is detected
   * @param delay - Maximum delay between taps in ms
   * @returns Touch event handlers
   */
  const createDoubleTapHandlers = useCallback(
    (
      onDoubleTap: () => void,
      delay: number = DEFAULT_DOUBLE_TAP_DELAY
    ): TouchHandlers => {
      const lastTapRef = { current: 0 };
      const startPosRef = { current: null as { x: number; y: number } | null };

      const handleTouchStart = (e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        
        startPosRef.current = { x: touch.clientX, y: touch.clientY };
      };

      const handleTouchEnd = (e: React.TouchEvent) => {
        if (!startPosRef.current) return;

        const touch = e.changedTouches[0];
        if (!touch) return;
        
        const deltaX = Math.abs(touch.clientX - startPosRef.current.x);
        const deltaY = Math.abs(touch.clientY - startPosRef.current.y);

        // Only count as tap if didn't move much
        if (deltaX < MOVEMENT_THRESHOLD && deltaY < MOVEMENT_THRESHOLD) {
          const now = Date.now();
          const timeSinceLastTap = now - lastTapRef.current;

          if (timeSinceLastTap < delay && timeSinceLastTap > 0) {
            // Double tap detected
            onDoubleTap();
            lastTapRef.current = 0; // Reset to prevent triple tap
          } else {
            // First tap
            lastTapRef.current = now;
          }
        }

        startPosRef.current = null;
      };

      const handleTouchMove = () => {
        // Touch move is handled in touchEnd by checking distance
      };

      const handleTouchCancel = () => {
        startPosRef.current = null;
      };

      return {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
        onTouchCancel: handleTouchCancel,
      };
    },
    []
  );

  return {
    isMobile,
    isTouch,
    createLongPressHandlers,
    createDoubleTapHandlers,
  };
}
